/**
 * DustSwapSection Component
 *
 * Shows dust swap details with token list displaying name, rate, and route.
 * Matches the simplified UI design showing swappable tokens and their conversion rates.
 * In staging/dev, also shows the user's accumulated swap PDA balance.
 */

import { Info, Wallet, ExternalLink } from 'lucide-react';

import type { TokenPriceData } from '@/hooks/useTokenPrices';
import { useSwapUserBalance } from '@/hooks/useSwapUserBalance';
import config from '@/config';

interface TokenInfo {
  mint: string;
  symbol: string;
  name: string;
  logoUrl?: string;
  uiAmount?: number;
}

interface DustSwapSectionProps {
  selectedSwappableTokens: TokenInfo[];
  prices: Map<string, TokenPriceData>;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRouteClick: (mint: string) => void;
  isLoading: boolean;
  onRefreshPrices: () => void;
  walletAddress?: string;
}

function formatSolValue(value: number): string {
  if (value === 0) return '0';
  if (value < 0.0001) return value.toFixed(6);
  if (value < 0.001) return value.toFixed(5);
  if (value < 0.01) return value.toFixed(4);
  return value.toFixed(2);
}

function formatRate(tokenAmount: number, solAmount: number): string {
  if (tokenAmount === 0 || solAmount === 0) return '0';
  const rate = solAmount / tokenAmount;
  if (rate < 0.0001) return rate.toFixed(8);
  if (rate < 0.01) return rate.toFixed(6);
  return rate.toFixed(4);
}

/**
 * Get Solscan explorer URL based on network
 */
function getSolscanUrl(address: string): string {
  const rpcUrl = config.SOLANA_RPC_URL || '';
  const isDevnet = rpcUrl.includes('devnet');
  const baseUrl = 'https://solscan.io/account';
  return isDevnet ? `${baseUrl}/${address}?cluster=devnet` : `${baseUrl}/${address}`;
}

export function DustSwapSection({
  selectedSwappableTokens,
  prices,
  isExpanded,
  onToggleExpand,
  onRouteClick,
  isLoading,
  walletAddress,
}: DustSwapSectionProps) {
  // Fetch swap PDA balance (only in staging/dev when enabled)
  const { balanceSol, pdaAddress, isEnabled: showPdaBalance } = useSwapUserBalance(walletAddress);

  if (selectedSwappableTokens.length === 0 && !isLoading && !showPdaBalance) {
    return null;
  }

  // Calculate total value
  const totalValue = selectedSwappableTokens.reduce((sum, token) => {
    const priceData = prices.get(token.mint);
    return sum + (priceData?.estimatedSolValue || 0);
  }, 0);

  return (
    <div className="mb-4">
      {/* PDA Balance Section (staging/dev only) */}
      {showPdaBalance && walletAddress && (
        <div
          className="mb-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-yellow-400" />
              <span
                className="text-xs text-yellow-400"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Swap PDA Balance
              </span>
            </div>
            <span
              className="text-sm font-bold text-yellow-400"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {balanceSol !== null ? `${formatSolValue(balanceSol)} SOL` : 'No account'}
            </span>
          </div>
          {pdaAddress && (
            <a
              href={getSolscanUrl(pdaAddress)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex items-center gap-1 text-xs text-gray-500 hover:text-yellow-400"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <span className="truncate">{pdaAddress.slice(0, 8)}...{pdaAddress.slice(-8)}</span>
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
            </a>
          )}
        </div>
      )}

      {/* Header */}
      {selectedSwappableTokens.length > 0 && (
        <button
          onClick={onToggleExpand}
          className="mb-2 flex w-full items-center justify-between"
        >
          <span
            className="text-sm text-gray-400"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Dust Swapping
          </span>
          <span
            className="text-lg font-bold"
            style={{ fontFamily: 'Outfit, sans-serif', color: '#4ADE80' }}
          >
            {formatSolValue(totalValue)} Credits
          </span>
        </button>
      )}

      {/* Token List - Only show when there are tokens */}
      {selectedSwappableTokens.length > 0 && (
        <div
          className="space-y-3 overflow-y-auto pr-1"
          style={{ maxHeight: '200px' }}
        >
          {selectedSwappableTokens.map(token => {
            const priceData = prices.get(token.mint);
            const dex = priceData?.quote?.routePlan?.[0]?.swapInfo?.label || 'Jupiter';
            const estimatedSol = priceData?.estimatedSolValue || 0;
            const rate = formatRate(token.uiAmount || 1, estimatedSol);

            return (
              <div key={token.mint} className="space-y-1">
                {/* Token Name */}
                <div
                  className="text-sm font-medium text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {token.name || token.symbol}
                </div>

                {/* Rate and Route Row */}
                <div className="flex items-center justify-between">
                  {/* Rate */}
                  <div>
                    <span
                      className="text-xs text-gray-500"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Rate
                    </span>
                    <div
                      className="text-xs text-gray-400"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      1 {token.symbol} = {rate} SOL
                    </div>
                  </div>

                  {/* Route */}
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <span
                        className="text-xs text-gray-500"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        Route
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="text-xs text-white"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          {token.symbol}
                        </span>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onRouteClick(token.mint);
                          }}
                          className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors hover:bg-white/10"
                          style={{
                            backgroundColor: 'rgba(80, 229, 248, 0.2)',
                            color: '#50E5F8',
                            fontFamily: 'Outfit, sans-serif',
                          }}
                        >
                          <Info className="h-3 w-3" />
                          {dex}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
